import React from 'react';
import { CustomRule } from 'devextreme-react/data-grid';
import { formatMessage } from 'devextreme/localization';

import StringHelper from '../../tools/StringHelper';

/** `'free'` = required + length; `'apiLabel'` = also the API-label charset (MetaApi). */
export type FieldNameMode = 'free' | 'apiLabel';

interface FieldNameRulesProps {
    /** Ids of the rows that are actually exposed; the rules only fire on those. */
    SelectionIds: number[];
    Mode: FieldNameMode;
    /** Row-data field carrying the row id (defaults to the store key shared by the 4 grids). */
    RowKeyField?: string;
}

/**
 * `apiLabel` (MetaApi's TechnicalName) stays short: it's a stable JSON key. `free` (Reporting / AI /
 * DataTable's DisplayName) is a free-text label sourced from arbitrary external field names, so it
 * gets the DB column's full width. Must track `FIELD_NAME_MAX_LENGTH` in
 * `logics/pathmodule/PathBeforeSaveValidation` and the backing entities' `[MaxLength]`.
 */
const MAX_LENGTH: Record<FieldNameMode, number> = {
    free: 256,
    apiLabel: 64,
};

/**
 * Shared inline validation for an exposed-field name column. It mirrors the
 * `BlockingForSave`-level checks of `checkPathBeforeSaveAndPublish` (see
 * `logics/pathmodule/PathBeforeSaveValidation`): required, length <= MAX_LENGTH[Mode] and — in
 * `apiLabel` mode — the API-label charset. Every rule is scoped to selected rows only,
 * exactly like the pre-save check.
 *
 * Duplicate-name detection is deliberately absent: it only blocks *publish*, so it stays
 * in the pre-save popup / Checking tab and never blocks a draft save from the grid.
 *
 * Rendered as the children of a DataGrid `<Column>`; DevExtreme collects the rules from
 * context, so wrapping them in this component is fine.
 */
export default function FieldNameRules({ SelectionIds, Mode, RowKeyField = 'SourceFieldReferenceId' }: FieldNameRulesProps) {

    const isExposed = (e: any): boolean => SelectionIds.includes(e?.data?.[RowKeyField]);

    return (
        <React.Fragment>
            <CustomRule
                validationCallback={(e: any) => {
                    if (!isExposed(e)) {
                        return true;
                    }
                    return ((e?.value as string) ?? '').trim().length > 0;
                }}
                message={formatMessage('PathValidation_FieldNameRequired')}
            />

            <CustomRule
                validationCallback={(e: any) => {
                    if (!isExposed(e)) {
                        return true;
                    }
                    return ((e?.value as string) ?? '').length <= MAX_LENGTH[Mode];
                }}
                message={formatMessage('PathValidation_FieldNameTooLong')}
            />

            {Mode === 'apiLabel' && (
                <CustomRule
                    validationCallback={(e: any) => {
                        const value = e?.value as string;
                        if (!isExposed(e) && (value === undefined || value === null || value.trim().length === 0)) {
                            return true;
                        }
                        return StringHelper.isValidApiLabel(value);
                    }}
                    message={formatMessage('PathValidation_FieldNameInvalidChars')}
                />
            )}
        </React.Fragment>
    );
}
